import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { PlusCircle, Edit, Trash2, Filter, BookOpen, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FunctionManagement = ({ onRefresh }) => {
  const [functions, setFunctions] = useState([]);
  const [allOralFunctions, setAllOralFunctions] = useState([]); // For dropdown - unfiltered
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFunction, setCurrentFunction] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    grade: '', 
    function_number: 1,
    exam_type: 'orals',
    function_suffix: '',
    oral_function_ref: null
  });
  
  // Filter states
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterExamType, setFilterExamType] = useState('all');
  
  // Grade options - including ETO
  const grades = [
    { value: 'MEO Class 1', label: 'MEO Class 1', category: 'Engineering' },
    { value: 'MEO Class 2', label: 'MEO Class 2', category: 'Engineering' },
    { value: 'MEO Class 4', label: 'MEO Class 4', category: 'Engineering' },
    { value: 'ETO', label: 'ETO', category: 'Engineering' },
    { value: 'Master', label: 'Master', category: 'Deck' },
    { value: 'Chief Mate', label: 'Chief Mate', category: 'Deck' },
    { value: '2MFG', label: '2MFG', category: 'Deck' }
  ];

  const examTypes = [
    { value: 'orals', label: 'Oral Exam', icon: BookOpen },
    { value: 'written', label: 'Written Exam', icon: FileText }
  ];

  // Fetch all oral functions once on mount (for the dropdown)
  useEffect(() => {
    fetchAllOralFunctions();
  }, []);

  useEffect(() => {
    fetchFunctions();
  }, [filterGrade, filterExamType]); // eslint-disable-line

  const fetchAllOralFunctions = async () => {
    try {
      // Fetch all oral functions (unfiltered) for the dropdown
      const response = await axios.get(`${API}/functions?exam_type=orals`);
      setAllOralFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch oral functions:', error);
    }
  };

  const fetchFunctions = async () => {
    try {
      setLoading(true);
      let url = `${API}/functions`;
      const params = new URLSearchParams();
      if (filterGrade !== 'all') params.append('grade', filterGrade);
      if (filterExamType !== 'all') params.append('exam_type', filterExamType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
      toast.error('Failed to load functions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Clean up optional fields - set to null instead of deleting
      if (!submitData.function_suffix) submitData.function_suffix = null;
      if (!submitData.oral_function_ref) submitData.oral_function_ref = null;
      
      await axios.post(`${API}/functions`, submitData);
      toast.success('Function created successfully');
      setDialogOpen(false);
      resetForm();
      fetchFunctions();
      fetchAllOralFunctions(); // Refresh oral functions for dropdown
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to create function:', error);
      toast.error('Failed to create function');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Clean up optional fields
      if (!submitData.function_suffix) submitData.function_suffix = null;
      if (!submitData.oral_function_ref) submitData.oral_function_ref = null;
      
      await axios.put(`${API}/admin/functions/${currentFunction.id}`, submitData);
      toast.success('Function updated successfully');
      setDialogOpen(false);
      setEditMode(false);
      setCurrentFunction(null);
      resetForm();
      fetchFunctions();
      fetchAllOralFunctions(); // Refresh oral functions for dropdown
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to update function:', error);
      toast.error('Failed to update function');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will fail if questions use this function.')) return;
    
    try {
      await axios.delete(`${API}/admin/functions/${id}`);
      toast.success('Function deleted successfully');
      fetchFunctions();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to delete function:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete function');
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      description: '', 
      grade: '', 
      function_number: 1,
      exam_type: 'orals',
      function_suffix: '',
      oral_function_ref: null
    });
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentFunction(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (func) => {
    setEditMode(true);
    setCurrentFunction(func);
    setFormData({ 
      name: func.name, 
      description: func.description || '', 
      grade: func.grade || '',
      function_number: func.function_number || 1,
      exam_type: func.exam_type || 'orals',
      function_suffix: func.function_suffix || '',
      oral_function_ref: func.oral_function_ref || null
    });
    setDialogOpen(true);
  };

  // Get function display name (e.g., "Function 3A" for written, "Function 3" for oral)
  const getFunctionDisplayNumber = (func) => {
    const suffix = func.function_suffix && func.function_suffix !== 'null' && func.function_suffix !== 'None' 
      ? func.function_suffix 
      : '';
    return `${func.function_number}${suffix}`;
  };

  // Get oral functions for the selected grade (for linking written functions)
  // Uses allOralFunctions which is fetched separately and not affected by view filters
  const getOralFunctionsForGrade = (grade) => {
    if (!grade) return [];
    return allOralFunctions.filter(f => f.grade === grade);
  };

  // Get category color
  const getCategoryColor = (grade) => {
    const gradeInfo = grades.find(g => g.value === grade);
    return gradeInfo?.category === 'Deck' ? 'bg-[#14B8A6]' : 'bg-[#0A7EA4]';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold">Function Management</h3>
            <p className="text-sm text-gray-600">Manage exam functions by grade, category, and exam type</p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={filterGrade} onValueChange={setFilterGrade}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  <SelectItem value="divider-eng" disabled className="text-xs text-gray-400">── Engineering ──</SelectItem>
                  {grades.filter(g => g.category === 'Engineering').map((grade) => (
                    <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                  ))}
                  <SelectItem value="divider-deck" disabled className="text-xs text-gray-400">── Deck ──</SelectItem>
                  {grades.filter(g => g.category === 'Deck').map((grade) => (
                    <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterExamType} onValueChange={setFilterExamType}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="orals">Oral</SelectItem>
                  <SelectItem value="written">Written</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Function
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg overflow-hidden">
                <DialogHeader>
                  <DialogTitle>{editMode ? 'Edit Function' : 'Create New Function'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={editMode ? handleUpdate : handleCreate} className="space-y-4 mt-4 min-w-0">
                  <div className="grid grid-cols-2 gap-4 min-w-0">
                    <div className="min-w-0">
                      <Label htmlFor="grade">Grade *</Label>
                      <Select
                        value={formData.grade}
                        onValueChange={(value) => setFormData({ ...formData, grade: value })}
                        required
                      >
                        <SelectTrigger className="mt-1 min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left">
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="header-eng" disabled className="text-xs text-gray-400 font-semibold">Engineering</SelectItem>
                          {grades.filter(g => g.category === 'Engineering').map((grade) => (
                            <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                          ))}
                          <SelectItem value="header-deck" disabled className="text-xs text-gray-400 font-semibold">Deck</SelectItem>
                          {grades.filter(g => g.category === 'Deck').map((grade) => (
                            <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="min-w-0">
                      <Label htmlFor="exam_type">Exam Type *</Label>
                      <Select
                        value={formData.exam_type}
                        onValueChange={(value) => setFormData({ ...formData, exam_type: value })}
                        required
                      >
                        <SelectTrigger className="mt-1 min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {examTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className="w-4 h-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="function_number">Function # *</Label>
                      {formData.exam_type === 'written' ? (
                        <>
                          <Input
                            id="function_number"
                            type="number"
                            min="1"
                            value={formData.function_number}
                            onChange={(e) => setFormData({ ...formData, function_number: parseInt(e.target.value) || 1 })}
                            required
                            className="mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">Use parent oral function's number</p>
                        </>
                      ) : (
                        <Input
                          id="function_number"
                          type="number"
                          min="1"
                          value={formData.function_number}
                          onChange={(e) => setFormData({ ...formData, function_number: parseInt(e.target.value) || 1 })}
                          required
                          className="mt-1"
                        />
                      )}
                    </div>
                    
                    {formData.exam_type === 'written' && (
                      <div>
                        <Label htmlFor="function_suffix">Suffix (Optional)</Label>
                        <Input
                          id="function_suffix"
                          value={formData.function_suffix}
                          onChange={(e) => setFormData({ ...formData, function_suffix: e.target.value.toUpperCase() })}
                          placeholder="A, B, etc."
                          maxLength={2}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">e.g., 3A, 3B (for future use)</p>
                      </div>
                    )}
                  </div>
                  
                  {formData.exam_type === 'written' && formData.grade && (
                    <div className="min-w-0 overflow-hidden">
                      <Label htmlFor="oral_function_ref">Link to Oral Function</Label>
                      <Select
                        value={formData.oral_function_ref || 'none'}
                        onValueChange={(value) => {
                          if (value === 'none') {
                            setFormData({ ...formData, oral_function_ref: null });
                          } else {
                            // Find the selected function and auto-set the function number
                            const selectedFunc = getOralFunctionsForGrade(formData.grade).find(f => f.id === value);
                            setFormData({ 
                              ...formData, 
                              oral_function_ref: value,
                              function_number: selectedFunc?.function_number || formData.function_number
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1 min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left">
                          <SelectValue placeholder="Select oral function to link" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No link</SelectItem>
                          {getOralFunctionsForGrade(formData.grade).map((func) => {
                            const suffix = func.function_suffix && func.function_suffix !== 'null' && func.function_suffix !== 'None' ? func.function_suffix : '';
                            return (
                              <SelectItem key={func.id} value={func.id}>
                                F{func.function_number}{suffix} - {func.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Links to oral function for topics</p>
                      {getOralFunctionsForGrade(formData.grade).length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No oral functions found for this grade</p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="name">Function Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Safety at Operational Level"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of the function"
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90">
                      {editMode ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grade</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Function #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {functions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    No functions found. {filterGrade !== 'all' || filterExamType !== 'all' ? 'Try adjusting filters.' : 'Create one to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                functions.map((func) => (
                  <TableRow key={func.id}>
                    <TableCell>
                      <Badge className={getCategoryColor(func.grade)}>{func.grade}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={func.exam_type === 'written' ? 'border-orange-500 text-orange-600' : 'border-blue-500 text-blue-600'}>
                        {func.exam_type === 'written' ? (
                          <><FileText className="w-3 h-3 mr-1" /> Written</>
                        ) : (
                          <><BookOpen className="w-3 h-3 mr-1" /> Oral</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-center">
                      F{getFunctionDisplayNumber(func)}
                      {func.oral_function_ref && (
                        <span className="text-xs text-gray-400 block">
                          → {(() => {
                            // Look up in allOralFunctions first, then in functions
                            const linkedFunc = allOralFunctions.find(f => f.id === func.oral_function_ref) 
                              || functions.find(f => f.id === func.oral_function_ref);
                            if (linkedFunc) {
                              const suffix = linkedFunc.function_suffix && linkedFunc.function_suffix !== 'null' && linkedFunc.function_suffix !== 'None' ? linkedFunc.function_suffix : '';
                              return `F${linkedFunc.function_number}${suffix}`;
                            }
                            return `Linked`;
                          })()}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{func.name}</TableCell>
                    <TableCell className="text-sm text-gray-600 hidden md:table-cell">{func.description || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openEditDialog(func)}
                          variant="ghost"
                          size="sm"
                          className="text-[#0A7EA4] hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(func.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
          <p>Total Functions: {functions.length}</p>
          <p>Oral: {functions.filter(f => f.exam_type === 'orals' || !f.exam_type).length}</p>
          <p>Written: {functions.filter(f => f.exam_type === 'written').length}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunctionManagement;